import { takeEvery, put, all } from 'redux-saga/effects';

import { loaderDecorator } from './decorators/loader-decorator';

import httpStatusCodes from '../constants/http-status-codes';
import referenceCodeWorkflowStatus from '../constants/reference-code-workflow-status';
import referenceStatus from '../constants/candidate-reference-statuses';

import * as types from '../actions/references/types';
import * as actions from '../actions/references';

import apiFetcher from '../tools/api-fetcher';
import handleError from '../tools/error-helper';
import { openPrintVersion } from '../tools/print-helper';

function* validateCandidateReferenceCode({ code }) {
  try {
    const { data } = yield apiFetcher.get(`references/${code}/status`);

    switch (data) {
      case referenceStatus.requested: {
        return yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.verified
          )
        );
      }
      case referenceStatus.completed: {
        return yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.used
          )
        );
      }
      case referenceStatus.declined: {
        return yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.declinedAlready
          )
        );
      }
      default: {
        return yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.notVerified
          )
        );
      }
    }
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound: {
        return yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.notFound
          )
        );
      }
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* declineCandidateReference({ code, reason }) {
  try {
    const { status } = yield validateCandidateReferenceCode({
      code
    });

    if (status !== referenceCodeWorkflowStatus.verified) {
      return;
    }

    yield put(
      actions.setReferenceCodeWorkflowStatus(
        referenceCodeWorkflowStatus.declining
      )
    );
    yield apiFetcher.delete(`references/${code}?reason=${reason}`);
    yield put(
      actions.setReferenceCodeWorkflowStatus(
        referenceCodeWorkflowStatus.declinedSuccessfully
      )
    );
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound: {
        yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.notFound
          )
        );
        break;
      }
      case httpStatusCodes.Gone: {
        yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.used
          )
        );
        break;
      }
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* getCandidateReferenceDetailsSummary({ code, reason }) {
  try {
    const { status } = yield validateCandidateReferenceCode({
      code
    });

    if (status !== referenceCodeWorkflowStatus.verified) {
      return;
    }

    const { data } = yield apiFetcher.get(`references/${code}`);
    yield put(actions.setCandidateReference(data));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound: {
        yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.notFound
          )
        );
        break;
      }
      case httpStatusCodes.Gone: {
        yield put(
          actions.setReferenceCodeWorkflowStatus(
            referenceCodeWorkflowStatus.used
          )
        );
        break;
      }
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* completeCandidateReference({ code, candidateReference }) {
  try {
    yield apiFetcher.put(`references/${code}`, candidateReference);
    yield put(
      actions.setReferenceCodeWorkflowStatus(
        referenceCodeWorkflowStatus.submittedSuccessfully
      )
    );
  } catch (exception) {
    handleError(exception);
  }
}

function* printCandidateReference({ code }) {
  try {
    yield openPrintVersion(`references/${code}/print-version`);
  } catch (exception) {
    handleError(exception, 'Failed to load candidate reference print version.');
  }
}

export default function* referencesSaga() {
  yield all([
    takeEvery(
      types.REFERENCE_VALIDATE_CANDIDATE_REFERENCE_CODE,
      loaderDecorator(validateCandidateReferenceCode, actions.setLoading)
    ),
    takeEvery(
      types.REFERENCE_DECLINE_CANDIDATE_REFERENCE,
      loaderDecorator(declineCandidateReference, actions.setLoading)
    ),
    takeEvery(
      types.REFERENCE_GET_CANDIDATE_REFERENCE_DETAILS_SUMMARY,
      loaderDecorator(getCandidateReferenceDetailsSummary, actions.setLoading)
    ),
    takeEvery(
      types.REFERENCE_COMPLETE_CANDIDATE_REFERENCE,
      loaderDecorator(completeCandidateReference, actions.setLoading)
    ),
    takeEvery(
      types.REFERENCE_PRINT_CANDIDATE_REFERENCE,
      loaderDecorator(printCandidateReference, actions.setLoading)
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/references.js